/**
 * storageClient.js
 *
 * HTTP client for the vgps_storage playback API — PLAYBACK-HANDOFF.md §4.5,
 * §5, §7.6, §7.7. Base URL: `REACT_APP_STORAGE_URL` in production; empty in
 * dev, where `package.json`'s `"proxy": "https://storage.vgps.vn"` forwards
 * relative `/ivis/...` fetches (CRA dev server only — see the dev/prod split
 * in each exported function).
 *
 * Auth: CMS JWT from `localStorage['access-token']`, TTL 900s (handoff §5).
 * Token is read LIVE inside every request (never captured once), and every
 * exported fetch here retries once through `refreshAccessToken()` on a 401
 * — the same token-refresh path `tokenSession.js` already uses for the rest
 * of the CMS (handoff §5: "If the CMS has a refresh path, wire a 401 ->
 * refresh -> retry once around the storage fetches").
 */
import { refreshAccessToken } from '../../../../tokenSession';

function storageBaseUrl() {
  const configured = process.env.REACT_APP_STORAGE_URL;
  return configured ? configured.replace(/\/+$/, '') : '';
}

function currentToken() {
  try {
    return localStorage.getItem('access-token') || '';
  } catch (_) {
    return '';
  }
}

function authHeaders() {
  const t = currentToken();
  return t ? { Authorization: 'Bearer ' + t } : {};
}

/**
 * fetch() with a live Authorization header and a single 401 -> refresh ->
 * retry cycle. Never put the token in a URL (handoff §5).
 */
async function authFetch(url, opts = {}) {
  const headers = { ...(opts.headers || {}), ...authHeaders() };
  let resp = await fetch(url, { cache: 'no-store', ...opts, headers });
  if (resp.status === 401) {
    try {
      await refreshAccessToken();
    } catch (_) {
      return resp; // no refresh token / refresh failed — surface the original 401
    }
    resp = await fetch(url, { cache: 'no-store', ...opts, headers: { ...(opts.headers || {}), ...authHeaders() } });
  }
  return resp;
}

/**
 * §4.5 listing: GET /ivis/storage/api/v0/device/recordings?device_serial=&from=&to=
 * `camera` in the response is 0-BASED (unlike playback_request.camera, §4.2/§4.3/trap#2).
 */
export async function listRecordingsWindow({ deviceSerial, sinceMs, untilMs }) {
  const url = `${storageBaseUrl()}/ivis/storage/api/v0/device/recordings` +
    `?device_serial=${encodeURIComponent(deviceSerial)}&from=${Math.round(sinceMs)}&to=${Math.round(untilMs)}`;
  const resp = await authFetch(url);
  if (!resp.ok) throw new Error(`listing HTTP ${resp.status}`);
  const body = await resp.json();
  return (body && body.items) || [];
}

/**
 * §7.6 cache-first check — required before every playback_request, not an
 * optimisation: a second view that skips this can make the device
 * re-upload a segment storage already has, overwriting the composed object
 * under a running player (trap #4 / PL-PBK2-13). Bounded at 2s (mock FE
 * `findCachedRecording()`); returns null (never throws) on timeout, network
 * error, or no match, so a storage hiccup never blocks playback.
 *
 * @param {object} args
 * @param {string} args.deviceSerial
 * @param {number} args.camIndex0    0-based camera index (differs from the
 *                                    wire's 1-based `camera` — trap #2).
 * @param {number} args.tsUs
 * @returns {Promise<object|null>} the matching `ready` listing row, or null
 */
export function findCachedRecording({ deviceSerial, camIndex0, tsUs }) {
  const tsMs = Math.round(tsUs / 1000);
  const windowMs = 10 * 60 * 1000; // generous query window; the real match test is the ±1s check below
  const req = listRecordingsWindow({ deviceSerial, sinceMs: tsMs - windowMs, untilMs: tsMs + windowMs })
    .then((items) => items.find((it) => (
      it.camera === camIndex0 && it.status === 'ready' && it.start_time != null &&
      Math.abs(new Date(it.start_time).getTime() - tsMs) <= 1000
    )) || null);
  const timeout = new Promise((resolve) => setTimeout(() => resolve('TIMEOUT'), 2000));
  return Promise.race([req, timeout]).then((result) => (result === 'TIMEOUT' ? null : result)).catch(() => null);
}

/**
 * The storage row for a card's segment, whatever its status (unlike
 * findCachedRecording(), which only returns `ready` rows because its caller
 * is deciding whether to skip the device). Used by the full-file download
 * path to obtain `download_url`. Prefers an exact `segment_uid` match (what
 * this card's own playback_ack told us); falls back to (camera, start_time)
 * for a card whose segment_uid we never learned (a cache-hit "Xem lại" that
 * never re-asked the device).
 */
export async function fetchSegmentRow({ deviceSerial, camIndex0, tsUs, segmentUid }) {
  const tsMs = Math.round(tsUs / 1000);
  const windowMs = 10 * 60 * 1000;
  const items = await listRecordingsWindow({ deviceSerial, sinceMs: tsMs - windowMs, untilMs: tsMs + windowMs });
  if (segmentUid) {
    const bySeg = items.find((it) => it.segment_uid === segmentUid);
    if (bySeg) return bySeg;
  }
  return items.find((it) => (
    it.camera === camIndex0 && it.start_time != null && Math.abs(new Date(it.start_time).getTime() - tsMs) <= 1000
  )) || null;
}

export function playlistUrlFor(segmentUid) {
  const path = `/ivis/storage/api/v0/device/recordings/${segmentUid}/playlist.m3u8`;
  const base = storageBaseUrl();
  // Always return an ABSOLUTE URL. In dev, REACT_APP_STORAGE_URL is unset
  // (CRA's package.json "proxy" forwards the relative fetch() itself just
  // fine) -- but this URL is also fed straight to parsePlaylist() as the
  // resolution base for relative #EXT-X-MAP/#EXTINF lines, and the WHATWG
  // URL constructor throws ("Invalid base URL") on ANY relative base
  // regardless of whether the line being resolved is itself already
  // absolute. Measured 2026-09-12: this broke every fresh (non-cache-hit)
  // live-device playback in local dev, immediately, on the pump's first
  // successful playlist fetch.
  return base ? `${base}${path}` : new URL(path, window.location.origin).toString();
}

/**
 * Parses the #EXT-X-MAP / #EXTINF / media-line shape of a growing playlist.
 * Ported from mock FE `parsePlaylist()` — shared by the MSE tier's
 * buffered-seconds source and the WebCodecs/WASM chunk pump, so there is
 * exactly one parser for this shape in the whole module.
 */
export function parsePlaylist(text, baseUrl) {
  let mapUri = null;
  const mediaUris = [];
  const durations = [];
  let totalDuration = 0;
  let endlist = false;
  let pendingDuration = 0;
  text.split('\n').map((l) => l.trim()).forEach((l) => {
    if (l.indexOf('#EXT-X-MAP:URI="') === 0) {
      const m = /URI="([^"]+)"/.exec(l);
      if (m) mapUri = baseUrl ? new URL(m[1], baseUrl).toString() : m[1];
    } else if (l.indexOf('#EXTINF:') === 0) {
      const d = parseFloat(l.slice('#EXTINF:'.length));
      pendingDuration = isNaN(d) ? 0 : d;
    } else if (l === '#EXT-X-ENDLIST') {
      endlist = true;
    } else if (l && l.charAt(0) !== '#') {
      mediaUris.push(baseUrl ? new URL(l, baseUrl).toString() : l);
      durations.push(pendingDuration);
      totalDuration += pendingDuration;
      pendingDuration = 0;
    }
  });
  return { mapUri, mediaUris, durations, totalDuration, endlist };
}

export function segmentFileName({ deviceSerial, camNum, tsUs }, suffix) {
  const d = new Date(tsUs / 1000);
  const pad2 = (n) => String(n).padStart(2, '0');
  const hhmmss = pad2(d.getHours()) + pad2(d.getMinutes()) + pad2(d.getSeconds());
  return `${deviceSerial}_CAM${camNum}_${hhmmss}${suffix}.mp4`;
}

function saveBlob(blob, fname) {
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
}

/**
 * §7.7 partial download: fetch #EXT-X-MAP + every media URI LISTED AT THE
 * MOMENT OF THE CLICK, concatenate the raw bytes, save. Plain byte
 * concatenation is a valid fragmented MP4 (ftyp+moov, then each chunk's own
 * moof+mdat) — no re-muxing. The URI list is snapshotted before the first
 * fetch starts so a chunk that lands mid-download cannot be appended into a
 * file already partly written.
 */
export async function downloadPartialSegment({ playlistUrl, deviceSerial, camNum, tsUs }) {
  const resp = await authFetch(playlistUrl);
  if (!resp.ok) throw new Error(`playlist HTTP ${resp.status}`);
  const text = await resp.text();
  const parsed = parsePlaylist(text, playlistUrl);
  if (!parsed.mapUri) throw new Error('playlist chưa có #EXT-X-MAP (init chunk)');
  const uris = [parsed.mapUri, ...parsed.mediaUris]; // snapshot NOW, before any fetch starts
  const buffers = await Promise.all(uris.map(async (u) => {
    const r = await authFetch(u);
    if (!r.ok) throw new Error(`HTTP ${r.status} tải ${u}`);
    return r.arrayBuffer();
  }));
  const blob = new Blob(buffers, { type: 'video/mp4' });
  const fname = segmentFileName({ deviceSerial, camNum, tsUs }, '_partial');
  saveBlob(blob, fname);
  return { fname, size: blob.size };
}

/**
 * §7.7 full download: prefer the listing row's presigned `download_url`
 * (needs NO Authorization header — trap #7: `window.open()` on the
 * Bearer-gated `/data` endpoint fails `missing_token` because a new tab
 * carries no headers). Fetch -> Blob -> `<a download>`, never a plain link
 * (the presigned URL's `Content-Disposition: inline` would just play it).
 */
export async function downloadFullSegment({ deviceSerial, camIndex0, camNum, tsUs, segmentUid }) {
  const row = await fetchSegmentRow({ deviceSerial, camIndex0, tsUs, segmentUid });
  let url, headers;
  if (row && row.download_url) {
    url = row.download_url;
    headers = {};
  } else if (segmentUid) {
    url = `${storageBaseUrl()}/ivis/storage/api/v0/device/recordings/${segmentUid}/data`;
    headers = authHeaders();
  } else {
    throw new Error('chưa biết segment_uid và storage không trả download_url');
  }
  const resp = await fetch(url, { cache: 'no-store', headers });
  if (!resp.ok) throw new Error(`HTTP ${resp.status} khi tải đoạn`);
  const buf = await resp.arrayBuffer();
  const blob = new Blob([buf], { type: 'video/mp4' });
  const fname = segmentFileName({ deviceSerial, camNum, tsUs }, '');
  saveBlob(blob, fname);
  return { fname, size: blob.size };
}

export { authFetch, authHeaders, storageBaseUrl };
